import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight, Facebook, Instagram, Linkedin } from "lucide-react";
import { LOGO_URL, CENTRES, CONTACTS } from "@/data/site";
import { CATEGORIES } from "@/data/formations";
import { useLead } from "@/components/common/LeadModal";

export default function Footer() {
  const { openLead } = useLead();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-brand-blue text-white" data-testid="site-footer">
      {/* Pre-footer CTA */}
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="-mt-20 rounded-3xl bg-brand-red px-8 py-12 shadow-[0_25px_50px_-12px_rgba(227,0,15,0.35)] sm:px-14 sm:py-14">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div>
              <h2 className="max-w-xl font-display text-3xl font-bold leading-tight sm:text-4xl">
                Prêt à démarrer votre projet de formation ?
              </h2>
              <p className="mt-3 max-w-lg text-white/85">
                Nos conseillers vous accompagnent de l'inscription au financement.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => openLead("devis")}
                className="rounded-full bg-white px-6 py-3 font-semibold text-brand-red transition-transform hover:-translate-y-0.5"
                data-testid="footer-cta-devis"
              >
                Demander un devis
              </button>
              <Link
                to="/contact"
                className="rounded-full border-2 border-white px-6 py-3 font-semibold text-white transition-colors hover:bg-white hover:text-brand-red"
                data-testid="footer-cta-contact"
              >
                Nous contacter
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <div className="inline-flex rounded-xl bg-white px-3 py-2">
              <img src={LOGO_URL} alt="EDUCASER GROUP" className="h-9 w-auto object-contain" />
            </div>
            <p className="mt-5 text-sm leading-relaxed text-white/70">
              Regroupement de centres de formation spécialisé dans les métiers du transport de
              marchandises, de voyageurs et de l'enseignement de la conduite.
            </p>
            <div className="mt-5 flex gap-3">
              {[
                { Icon: Facebook, href: "https://www.facebook.com/share/1BsYsVrt95/?mibextid=wwXIfr", label: "Facebook" },
                { Icon: Instagram, href: "https://www.instagram.com/educaser1?igsh=Z2xsOGdmcHdlY29s", label: "Instagram" },
                { Icon: Linkedin, href: "https://www.linkedin.com/company/educasergroup/", label: "LinkedIn" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-brand-red"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
            <div className="mt-6" data-testid="footer-transparence">
              <p className="text-xs leading-relaxed text-white/50">
                EDUCASER GROUP fédère des organismes de formation juridiquement indépendants.
                Les prestations, contrats, certifications, agréments et résultats relèvent du centre
                sélectionné.
              </p>
            </div>
          </div>

          {/* Formations */}
          <div>
            <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-white/90">
              Formations
            </h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              {CATEGORIES.map((c) => (
                <li key={c.slug}>
                  <Link to={`/nos-formations/${c.slug}`} className="text-white/70 transition-colors hover:text-white">
                    {c.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/financements" className="text-white/70 transition-colors hover:text-white">
                  Financements
                </Link>
              </li>
            </ul>
          </div>

          {/* Accès rapide */}
          <div>
            <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-white/90">
              Accès rapide
            </h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              {[
                ["Accueil", "/"],
                ["Le Groupe", "/le-groupe"],
                ["Nos centres", "/nos-centres"],
                ["Résultats", "/resultats"],
                ["Documents utiles", "/documents-utiles"],
                ["Signalement", "/signalement"],
                ["Contact", "/contact"],
                ["Mentions légales", "/mentions-legales"],
                ["Politique de confidentialité", "/politique-de-confidentialite"],
                ["Gestion des cookies", "/cookies"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="text-white/70 transition-colors hover:text-white">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-white/90">
              Contact
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-2">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand-red-light" />
                <span>
                  <a href="tel:0766940526" className="font-medium text-white transition-colors hover:text-brand-red-light">07 66 94 05 26</a>
                  {" "}— Formation moniteur
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand-red-light" />
                <span>
                  <a href="tel:0781524873" className="font-medium text-white transition-colors hover:text-brand-red-light">07 81 52 48 73</a>
                  {" "}— Formation groupe lourd
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand-red-light" />
                <span>
                  <a href="tel:0612578108" className="font-medium text-white transition-colors hover:text-brand-red-light">06 12 57 81 08</a>
                  {" "}— Centre de Choisy-le-Roi
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-brand-red-light" />
                <span>{CONTACTS.enseignants.email}</span>
              </li>
              {CENTRES.map((c) => (
                <li key={c.id} className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-red-light" />
                  <span>{c.adresse}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-sm text-white/60 md:flex-row md:items-center">
          <p>© {year} EDUCASER GROUP. Tous droits réservés.</p>
          <div className="flex flex-wrap items-center gap-4">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white">
              <span className="h-2 w-2 rounded-full bg-brand-red-light" /> Certifié Qualiopi
            </span>
            <Link to="/mentions-legales" className="transition-colors hover:text-white">Mentions légales</Link>
            <Link to="/politique-de-confidentialite" className="inline-flex items-center gap-1 transition-colors hover:text-white">
              Confidentialité <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
